import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaGlobe, FaTimes, FaMapMarkerAlt } from 'react-icons/fa';
import { weatherApi } from '../services/weatherApi';

const SearchBar = ({ onLocationSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  const handleSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setShowResults(false);
      setHasSearched(false);
      return;
    }

    if (searchQuery.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setSearchError(false);
    try {
      console.log('Searching for:', searchQuery);
      const searchResults = await weatherApi.searchLocation(searchQuery);
      console.log('Search results:', searchResults);
      setResults(searchResults);
      setShowResults(true);
      if (searchResults.length === 0) {
        setSearchError(true);
      }
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setShowResults(true);
      setSearchError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    console.log('Input changed to:', value);
    setQuery(value);
    
    // Debounce search
    clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {
      console.log('Triggering search for:', value);
      handleSearch(value);
    }, 500);
  };

  const handleLocationSelect = (location) => {
    setQuery(location.displayName);
    setShowResults(false);
    onLocationSelect(location);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    setHasSearched(false);
    setSearchError(false);
  };

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative z-[100]" ref={resultsRef}>
      <motion.div 
        className="bg-white/25 backdrop-blur-xl border border-white/40 rounded-3xl p-6 shadow-2xl relative"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/20 rounded-xl">
              <FaGlobe className="text-xl text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800">Search Anywhere in the World</h2>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setQuery('Mumbai');
                handleSearch('Mumbai');
              }}
              className="text-xs bg-blue-500/20 text-blue-600 px-2 py-1 rounded-lg hover:bg-blue-500/30 transition-colors"
            >
              Test Mumbai
            </button>
            <button
              onClick={() => {
                setQuery('London');
                handleSearch('London');
              }}
              className="text-xs bg-blue-500/20 text-blue-600 px-2 py-1 rounded-lg hover:bg-blue-500/30 transition-colors"
            >
              Test London
            </button>
          </div>
        </div>
        
        <div className="relative">
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={handleInputChange}
              placeholder="Search cities, countries, landmarks..."
              className="bg-white/30 backdrop-blur-md border border-white/40 rounded-2xl w-full pl-12 pr-12 py-4 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg"
            />
            {query && (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes />
              </button>
            )}
          </div>
          
          <AnimatePresence>
            {showResults && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                className="absolute top-full left-0 right-0 mt-3 bg-white/30 backdrop-blur-xl border border-white/40 rounded-2xl max-h-60 overflow-y-auto shadow-2xl z-[9999]"
                style={{ zIndex: 9999 }}
              >
                {loading ? (
                  <div className="p-6 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                    <p className="text-sm">Searching locations...</p>
                  </div>
                ) : results.length > 0 ? (
                  results.map((location, index) => (
                    <motion.button
                      key={index}
                      onClick={() => handleLocationSelect(location)}
                      className="w-full px-6 py-4 text-left hover:bg-white/30 transition-all duration-200 border-b border-white/20 last:border-b-0 first:rounded-t-2xl last:rounded-b-2xl"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center space-x-3">
                        <FaMapMarkerAlt className="text-blue-500 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-gray-800">{location.name}</div>
                          <div className="text-sm text-gray-500">{location.country}</div>
                        </div>
                      </div>
                    </motion.button>
                  ))
                ) : hasSearched && !loading && searchError ? (
                  <div className="p-6 text-center text-gray-500">
                    <FaMapMarkerAlt className="text-2xl mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No locations found for "{query}"</p>
                    <p className="text-xs mt-1">Try searching for a city name or country</p>
                    <div className="mt-3 text-xs text-gray-400">
                      <p>Examples: Mumbai, Delhi, London, New York</p>
                    </div>
                  </div>
                ) : null}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default SearchBar;
